/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import ProtectedRoute from "ProtectedRoute";
import Icon from "@mui/material/Icon";
// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import ScheduleBuilder from "layouts/ScheduleBuilder";
import Profile from "layouts/profile";
import YourKitchen from "layouts/your_kitchen";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import Admin from "layouts/admin";
import Onboarding from "layouts/onboarding";
import IndexPage from "layouts/module";
import TermsAndConditions from "layouts/authentication/sign-up/terms";
import PrivacyPolicy from "layouts/authentication/sign-up/privacy";
import RequestPasswordReset from "layouts/authentication/reset-password/cover";
import ResetPassword from "layouts/authentication/reset-password/cover/change_password";

import Support_the_project from "layouts/support_the_project";
import My_schedules from "layouts/my_schedules";
import Cookbook from "layouts/cookbook";
import BrowseRecipes from "layouts/browse";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    // Directly under Dashboard, and in the nav rather than buried in account
    // settings: the technique ladder is the strongest thing in the product and
    // it used to sit below the password field on /profile with no way to reach
    // it. If it isn't in the nav, it doesn't exist.
    type: "collapse",
    name: "Your Kitchen",
    key: "your-kitchen",
    icon: <Icon fontSize="small">local_fire_department</Icon>,
    route: "/your-kitchen",
    component: (
      <ProtectedRoute>
        <YourKitchen />
      </ProtectedRoute>
    ),
  },
  {
    // Sits above "My Cooking" deliberately: it is the answer to "does this app
    // have food I'd eat?", which a new user asks before they have any cooking
    // of their own to look at.
    type: "collapse",
    name: "Browse Recipes",
    key: "browse",
    icon: <Icon fontSize="small">restaurant_menu</Icon>,
    route: "/browse",
    component: (
      <ProtectedRoute>
        <BrowseRecipes />
      </ProtectedRoute>
    ),
  },
  { type: "title", title: "My Cooking", key: "title-cooking" },
  {
    type: "collapse",
    name: "My Schedules",
    key: "my_schedules",
    icon: <Icon fontSize="small">event_note</Icon>,
    route: "/my_schedules",
    component: (
      <ProtectedRoute>
        <My_schedules />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Schedule Builder",
    key: "schedule-builder",
    icon: <Icon fontSize="small">edit_calendar</Icon>,
    route: "/schedule-builder",
    component: (
      <ProtectedRoute>
        <ScheduleBuilder />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Cookbook",
    key: "cookbook",
    icon: <Icon fontSize="small">auto_stories</Icon>,
    route: "/cookbook",
    component: (
      <ProtectedRoute>
        <Cookbook />
      </ProtectedRoute>
    ),
  },
  {
    type: "route",
    route: "/profile",
    component: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    ),
  },
  {
    type: "route",
    route: "/support",
    component: (
      <ProtectedRoute>
        <Support_the_project />
      </ProtectedRoute>
    ),
  },
  {
    type: "route",
    key: "admin",
    route: "/admin",
    component: (
      <ProtectedRoute roles={["admin"]}>
        <Admin />
      </ProtectedRoute>
    ),
  },
  {
    type: "route", // will NOT show in sidenav
    route: "/module/:programId",
    component: (
      <ProtectedRoute>
        <IndexPage />
      </ProtectedRoute>
    ),
  },
  {
    type: "route", // SignIn
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "route", // SignUp
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
  {
    type: "route",
    route: "/terms",
    component: <TermsAndConditions />,
  },
  {
    type: "route",
    route: "/privacy",
    component: <PrivacyPolicy />,
  },

  {
    type: "route",
    route: "/authentication/reset-password",
    component: <RequestPasswordReset />,
  },

  {
    type: "route",
    route: "/authentication/reset-password/confirm",
    component: <ResetPassword />,
  },

  {
    type: "route", // Onboarding hidden from sidenav
    route: "/onboarding",
    component: (
      <ProtectedRoute>
        <Onboarding />
      </ProtectedRoute>
    ),
  },
];

export default routes;
